import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import EditOutlined from '@mui/icons-material/EditOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import { surface, tone } from '../timeclock/tcFormat';
import { hours, teamError, teamPost } from './teamFormat';

/**
 * Balances for a year. Everyone sees their own cards; management and Els get
 * the table of all colleagues, and Jeroen and Els can enter the leave right.
 */
const TeamBalanceTab = ({ t, lang, theme, token, me, reloadKey, year, notify, failed, showOwn = true }) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [edit, setEdit] = useState(null);
    const [again, setAgain] = useState(0);

    useEffect(() => {
        let alive = true;
        setBusy(true);
        teamPost('balances', token, { year }).then((response) => {
            if (!alive) return;
            setBusy(false);
            if (response?.success) {
                setData(response.data);
                setError('');
            } else {
                setError(failed(response));
            }
        });
        return () => { alive = false; };
    }, [token, year, reloadKey, again, failed]);

    const s = surface(theme);
    const rows = data?.rows || [];
    const mine = rows.find((r) => r.staff.id === me.staff.id);
    const month = new Date().getMonth() + 1;
    // Recup may only go below zero in November and December.
    const recupTone = (v) => (v < 0 ? (year === new Date().getFullYear() && month >= 11 ? tone.warn : tone.bad) : undefined);

    const card = (title, main, mainColor, lines) => (
        <Box sx={{ ...s.card, p: 2.25, flex: '1 1 220px', minWidth: 0 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: s.faint, mb: 0.75 }}>{title}</Typography>
            <Typography sx={{ fontSize: 28, fontWeight: 700, lineHeight: 1.15, color: mainColor, fontVariantNumeric: 'tabular-nums' }}>{main}</Typography>
            <Box sx={{ mt: 1.25, display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 0.4, columnGap: 2, fontSize: 13.5 }}>
                {lines.map(([label, value]) => (
                    <React.Fragment key={label}>
                        <Box sx={{ color: 'text.secondary' }}>{label}</Box>
                        <Box sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{value}</Box>
                    </React.Fragment>
                ))}
            </Box>
        </Box>
    );

    const own = mine && (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
            {card(t('team_card_leave', 'Leave'),
                mine.leave.left === null ? '—' : hours(mine.leave.left, lang), mine.leave.left < 0 ? tone.bad : undefined, [
                    [t('team_leave_right', 'Right') + ' ' + year, mine.leave.right === null ? t('team_not_entered', 'not entered yet') : hours(mine.leave.right, lang)],
                    ...(mine.leave.adjust ? [[t('team_adjust', 'Correction'), hours(mine.leave.adjust, lang)]] : []),
                    [t('team_leave_used', 'Planned and approved'), hours(mine.leave.used, lang)],
                    [t('team_leave_pending', 'Requested'), hours(mine.leave.pending, lang)],
                ])}
            {card(t('team_card_recup', 'Recup'),
                hours(mine.recup.available, lang), recupTone(mine.recup.available), [
                    [t('team_recup_earned', 'Earned until today'), hours(mine.recup.earned_to_date, lang)],
                    [t('team_recup_taken', 'Taken and planned'), hours(mine.recup.taken + mine.recup.pending, lang)],
                    [t('team_recup_year_end', 'At the end of the year'), hours(mine.recup.year_end, lang)],
                ])}
            {card(t('team_card_replacement', 'Replacement days'),
                String(mine.replacement.left), mine.replacement.left < 0 ? tone.bad : undefined, [
                    [t('team_replacement_right', 'Holidays on a weekend'), String(mine.replacement.right)],
                    [t('team_replacement_used', 'Planned'), String(mine.replacement.used)],
                    [t('team_sick_days', 'Sick days'), String(mine.sick_days)],
                ])}
        </Box>
    );

    const th = { fontSize: 12, fontWeight: 700, color: s.faint, textAlign: 'right', px: 1.5, py: 1, whiteSpace: 'nowrap' };
    const td = { fontSize: 13.5, textAlign: 'right', px: 1.5, py: 0.9, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', borderTop: s.line };

    const table = me.rights.sees_all && rows.length > 1 && (
        <Box sx={{ ...s.card, overflowX: 'auto' }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        <Box component="th" sx={{ ...th, textAlign: 'left' }}>{t('team_col_name', 'Colleague')}</Box>
                        <Box component="th" sx={th}>{t('team_leave_right', 'Right')}</Box>
                        <Box component="th" sx={th}>{t('team_col_used', 'Approved')}</Box>
                        <Box component="th" sx={th}>{t('team_leave_pending', 'Requested')}</Box>
                        <Box component="th" sx={th}>{t('team_col_left', 'Left')}</Box>
                        <Box component="th" sx={th}>{t('team_col_recup_now', 'Recup today')}</Box>
                        <Box component="th" sx={th}>{t('team_col_recup_end', 'Recup year end')}</Box>
                        <Box component="th" sx={th}>{t('team_col_replacement', 'Repl. days left')}</Box>
                        <Box component="th" sx={th}>{t('team_col_sick', 'Sick days')}</Box>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.staff.id}>
                            <Box component="td" sx={{ ...td, textAlign: 'left', fontWeight: r.staff.id === me.staff.id ? 700 : 400 }}>{r.staff.name}</Box>
                            <Box component="td" sx={td}>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                    {r.leave.right === null
                                        ? <Box component="span" sx={{ color: tone.warn }}>{t('team_not_entered', 'not entered yet')}</Box>
                                        : hours(r.leave.right, lang)}
                                    {me.rights.edit_allowance && (
                                        <Tooltip title={t('team_edit_right', 'Enter the leave right')}>
                                            <IconButton size="small" onClick={() => setEdit({ staff: r.staff, value: r.leave.right ?? '' })} sx={{ p: 0.25 }}>
                                                <EditOutlined sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            </Box>
                            <Box component="td" sx={td}>{hours(r.leave.used, lang)}</Box>
                            <Box component="td" sx={{ ...td, color: r.leave.pending ? tone.info : s.faint }}>{hours(r.leave.pending, lang)}</Box>
                            <Box component="td" sx={{ ...td, fontWeight: 600, color: r.leave.left < 0 ? tone.bad : undefined }}>{r.leave.left === null ? '—' : hours(r.leave.left, lang)}</Box>
                            <Box component="td" sx={{ ...td, color: recupTone(r.recup.available) }}>{hours(r.recup.available, lang)}</Box>
                            <Box component="td" sx={{ ...td, color: recupTone(r.recup.year_end) }}>{hours(r.recup.year_end, lang)}</Box>
                            <Box component="td" sx={td}>{r.replacement.left} / {r.replacement.right}</Box>
                            <Box component="td" sx={td}>{r.sick_days}</Box>
                        </tr>
                    ))}
                </tbody>
            </Box>
        </Box>
    );

    return (
        <Box>
            {error && <Typography sx={{ color: tone.bad, fontSize: 13.5, mb: 1 }}>{error}</Typography>}
            {busy && !data && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>}
            {showOwn && own}
            {table}
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 1.5, maxWidth: 760 }}>
                {t('team_balance_note', 'Recup: +0.4 h for every day worked by roster; nothing on days with leave, recup, a replacement day, sickness or special leave. It may only go below zero in November and December. Nothing carries over to next year.')}
            </Typography>

            <AllowanceDialog open={!!edit} edit={edit} year={year} t={t} lang={lang} token={token}
                onClose={() => setEdit(null)}
                onSaved={() => { setEdit(null); notify(t('team_saved', 'Saved'), true); setAgain((k) => k + 1); }} />
        </Box>
    );
};

const AllowanceDialog = ({ open, edit, year, t, token, onClose, onSaved }) => {
    const [value, setValue] = useState('');
    const [remark, setRemark] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            setValue(edit?.value === null || edit?.value === undefined ? '' : String(edit.value).replace('.', ','));
            setRemark('');
            setError('');
        }
    }, [open, edit]);

    const number = parseFloat(String(value).replace(',', '.'));
    const valid = !isNaN(number) && number >= 0 && number <= 500;

    const save = () => {
        setBusy(true);
        teamPost('saveAllowance', token, { staff_id: edit.staff.id, year, leave_h: number, remark }).then((response) => {
            setBusy(false);
            if (response?.success) onSaved();
            else setError(teamError(t, response));
        });
    };

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontSize: 17, fontWeight: 700 }}>{t('team_edit_right', 'Enter the leave right')} · {edit?.staff?.name} · {year}</DialogTitle>
            <DialogContent>
                <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mb: 2 }}>
                    {t('team_right_hint', 'Legal leave in hours, as the social secretariat calculated it from last year\'s work. Full-time: 160 h.')}
                </Typography>
                <TextField autoFocus fullWidth size="small" label={t('team_hours', 'Hours')} value={value} onChange={(e) => setValue(e.target.value)}
                    error={value !== '' && !valid} inputProps={{ inputMode: 'decimal' }} sx={{ mb: 2 }} />
                <TextField fullWidth size="small" label={t('team_remark', 'Remark')} value={remark} onChange={(e) => setRemark(e.target.value)}
                    placeholder={t('team_right_remark_hint', 'e.g. part-time from March')} inputProps={{ maxLength: 255 }} />
                {error && <Typography sx={{ color: tone.bad, fontSize: 13.5, mt: 1.5 }}>{error}</Typography>}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>{t('cancel', 'Cancel')}</Button>
                <Button variant="contained" disableElevation onClick={save} disabled={!valid || busy} sx={{ textTransform: 'none' }}>
                    {busy ? t('tc_busy', 'One moment…') : t('team_save', 'Save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TeamBalanceTab;
